import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import kabbiLogo from '@/assets/images/kabbi-logo-gelb.png';
import AuthMapPanel from '@/components/AuthMapPanel';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { registerFormSchema, type RegisterFormInput } from '@kabbi/shared/schemas/forms';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { zodFormResolver } from '@/lib/zodResolver';
import { firstErrorMessage } from '@/lib/formErrors';
import i18n from '../i18n';

// Same VITE_API_URL handling as lib/api.ts and authStore.ts — split-domain
// prod points this at https://api.kabbi.io/api; dev falls back to same-origin.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

// Hoisted to module scope so the resolver is built once per module load
// (RHF reads `options.resolver` on mount).
const registerResolver = zodFormResolver(registerFormSchema);

/**
 * Self-registration / "create account" page.
 *
 * Mirrors the login layout: a frosted-glass form card on a soft gradient
 * panel (left) beside our decorative Bamberg streetmap (right, hidden below
 * `md`). Six fields (firstName, lastName, email, phone optional, password,
 * confirmPassword) are validated against the shared `registerFormSchema`
 * whose refine() pins password === confirmPassword. On success the API
 * returns 201 + emails a verification code and the SPA goes to /verify-email.
 */
export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentLang = i18n.language;

  // RHF form. defaultValues keep every text field as a controlled empty
  // string, matching the schema's input shape.
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormInput>({
    resolver: registerResolver,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  // `isSubmitting` from RHF replaces a manual `loading` state — every submit
  // path here goes through `handleSubmit`, so the form's tracker suffices.
  const [showPassword, setShowPassword] = useState(false);

  /** Switch the active i18n language and persist the choice for next visit. */
  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('kabbi_lang', lang);
  };

  /**
   * Submit handler invoked after schema validation passes. Strips the
   * client-only `confirmPassword`, converts empty phone to undefined, and
   * posts to `/api/auth/register`. Maps 409 + generic failures to toasts.
   */
  const onSubmit = async (data: RegisterFormInput) => {
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone.trim() || undefined,
          password: data.password,
          locale: currentLang,
        }),
      });
      if (!res.ok) {
        toast.error(res.status === 409 ? t('register.errorEmailTaken') : t('register.errorGeneric'));
        return;
      }
      navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);
    } catch {
      toast.error(t('register.errorGeneric'));
    }
  };

  /**
   * Validation-failed handler. Surfaces the first invalid field's i18n key
   * via toast — field visuals come from `aria-invalid` on each input.
   */
  const onInvalid = (errs: typeof errors) => {
    const message = firstErrorMessage(errs);
    if (message) toast.error(t(message));
  };

  /** Language toggle button — active uses the navy primary, idle is muted. */
  const LangButton = ({ code, label }: { code: string; label: string }) => (
    <button
      type="button"
      onClick={() => switchLang(code)}
      aria-current={currentLang === code ? 'true' : undefined}
      className={`rounded-lg px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
        currentLang === code
          ? 'bg-primary text-primary-foreground'
          : 'bg-card/70 text-muted-foreground hover:text-primary'
      }`}
    >
      {label}
    </button>
  );

  /** Shared uppercase field-label class, matching the login form. */
  const labelClass = 'mb-1.5 block text-[11.5px] font-semibold uppercase tracking-[0.03em] text-muted-foreground';
  /** Shared input class, matching the login form. */
  const inputClass = 'h-11 rounded-xl bg-muted text-sm';

  return (
    <div className="flex min-h-svh w-full bg-muted">
      {/* ── LEFT: create-account card on a soft gradient panel ── */}
      <div className="relative flex w-full flex-col items-center justify-center bg-gradient-to-br from-secondary to-muted px-8 py-12 md:w-[44%] md:shrink-0">
        {/* Language switcher, top-left */}
        <div className="absolute left-8 top-6 flex gap-1">
          <LangButton code="de" label="DE" />
          <LangButton code="en" label="EN" />
        </div>

        {/* Frosted-glass card */}
        <section
          className="w-full max-w-[462px] rounded-3xl border border-border/60 bg-card/90 p-2.5 shadow-2xl backdrop-blur-xl"
          aria-labelledby="register-title"
        >
          <img src={kabbiLogo} alt="Kabbi" className="mb-8 h-9 w-auto" />

          <h1 id="register-title" className="mb-1.5 text-2xl font-bold tracking-tight text-foreground">
            {t('register.title')}
          </h1>
          <p className="mb-7 text-sm text-muted-foreground">{t('register.subtitle')}</p>

          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-4" noValidate>
            {/* First / last name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <Label htmlFor="firstName" className={labelClass}>{t('register.firstName')}</Label>
                <Input
                  id="firstName"
                  placeholder={t('register.firstNamePlaceholder')}
                  disabled={isSubmitting}
                  aria-invalid={errors.firstName ? 'true' : undefined}
                  className={inputClass}
                  {...register('firstName')}
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="lastName" className={labelClass}>{t('register.lastName')}</Label>
                <Input
                  id="lastName"
                  placeholder={t('register.lastNamePlaceholder')}
                  disabled={isSubmitting}
                  aria-invalid={errors.lastName ? 'true' : undefined}
                  className={inputClass}
                  {...register('lastName')}
                />
              </div>
            </div>

            {/* Email */}
            <div className="flex flex-col">
              <Label htmlFor="email" className={labelClass}>{t('register.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                disabled={isSubmitting}
                aria-invalid={errors.email ? 'true' : undefined}
                className={inputClass}
                {...register('email')}
              />
            </div>

            {/* Phone (optional) */}
            <div className="flex flex-col">
              <Label htmlFor="phone" className={labelClass}>{t('register.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                placeholder={t('register.phonePlaceholder')}
                disabled={isSubmitting}
                aria-invalid={errors.phone ? 'true' : undefined}
                className={inputClass}
                {...register('phone')}
              />
            </div>

            {/* Password */}
            <div className="flex flex-col">
              <Label htmlFor="password" className={labelClass}>{t('register.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('register.passwordPlaceholder')}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  aria-invalid={errors.password ? 'true' : undefined}
                  className={`${inputClass} pr-11`}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className="flex flex-col">
              <Label htmlFor="confirmPassword" className={labelClass}>{t('register.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t('register.confirmPasswordPlaceholder')}
                autoComplete="new-password"
                disabled={isSubmitting}
                aria-invalid={errors.confirmPassword ? 'true' : undefined}
                className={inputClass}
                {...register('confirmPassword')}
              />
            </div>

            {/* Submit — brand-yellow primary action */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 h-12 w-full gap-2.5 rounded-xl bg-brand text-[15px] font-bold text-brand-foreground shadow-md hover:bg-brand hover:brightness-105"
            >
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t('register.loading')}
                </>
              ) : (
                <>
                  <span>{t('register.submit')}</span>
                  <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.5} />
                </>
              )}
            </Button>
          </form>

          {/* Back to login */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t('register.loginLink')}{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              {t('register.loginLinkAction')}
            </Link>
          </p>
        </section>
      </div>

      {/* ── RIGHT: decorative live map panel (hidden on small screens) ── */}
      <AuthMapPanel />
    </div>
  );
}
